import { useState, useEffect, useMemo, useRef } from 'react'
import { woPayloadStore } from '../lib/ebisWoStore'
import { supabase } from '../lib/supabase'
import { isOverviewItem } from '../lib/overviewTasks'
import { calcLaborCost } from '../lib/ebisPush'
import EbisPushConfirmModal from './EbisPushConfirmModal'
import { findOverrideForTask, searchTaskHistory, getFamilyRegs, computeHistoryStats } from '../lib/woHistoryUtils'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || ''
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || ''
const DUE_LIST_URL = `${SUPABASE_URL}/functions/v1/traxxall-due-list`
const ACTUALS_URL  = `${SUPABASE_URL}/functions/v1/traxxall-actuals`
const EBIS_WO_URL  = `${SUPABASE_URL}/functions/v1/ebis-workorder`

// Module-level cache keyed by registration — survives tab switches for the lifetime of the page session
const _cache = {} // { [registration]: { items: [], actuals: [], lastFetched: Date } }
let _ebisAllRows = null // full EBIS work-order listing; null until first successful fetch
const _woItemsCache = {} // { [workOrderNumber]: {text,status}[] } — discrepancy items from cached WO detail
const _overridesCache = {} // { [registration]: { [itemKey]: overrideRow } }

// Pre-fetch Traxxall due list + actuals for a registration and populate _cache.
// Used by the fleet calendar view so data is ready before the user expands an aircraft.
// Safe to call concurrently for multiple registrations — each writes to its own cache key.
export async function prefetchTraxxallData(registration) {
  if (!registration) return null
  if (_cache[registration]?.items && _cache[registration]?.actuals) {
    return { items: _cache[registration].items, actuals: _cache[registration].actuals }
  }
  // Check Supabase persistent cache before hitting live API
  try {
    const { data: row } = await supabase
      .from('traxxall_due_cache')
      .select('items, actuals, fetched_at')
      .eq('registration', registration)
      .maybeSingle()
    if (row?.items?.length) {
      const actuals = parseActuals(row.actuals || [])
      _cache[registration] = { items: row.items, actuals, lastFetched: new Date(row.fetched_at) }
      return { items: row.items, actuals }
    }
  } catch {}
  // Fall through to live API
  const headers = { Authorization: `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' }
  const [dueRes, actRes] = await Promise.allSettled([
    fetch(`${DUE_LIST_URL}?registration=${encodeURIComponent(registration)}&days=730`, { headers }),
    fetch(`${ACTUALS_URL}?registration=${encodeURIComponent(registration)}`, { headers }),
  ])
  const dueData = dueRes.status === 'fulfilled' && dueRes.value.ok ? await dueRes.value.json() : []
  const actData = actRes.status === 'fulfilled' && actRes.value.ok ? await actRes.value.json() : []
  const items   = Array.isArray(dueData) ? dueData : []
  const actuals = parseActuals(actData)
  _cache[registration] = { ..._cache[registration], items, actuals, lastFetched: new Date() }
  return { items, actuals }
}

// Fetch the full EBIS work-order listing (all open WOs, all registrations) and cache it.
// Reuses _ebisAllRows if already fetched this session.
export async function fetchEbisListing() {
  if (_ebisAllRows) return _ebisAllRows
  try {
    const r = await fetch(`${EBIS_WO_URL}?endpoint=listing`, {
      method:  'POST',
      headers: { Authorization: `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' },
      body:    JSON.stringify({ WoStatusID: -1, WoTypeID: 1 }),
    })
    const data = r.ok ? await r.json() : []
    _ebisAllRows = Array.isArray(data) ? data : []
    return _ebisAllRows
  } catch {
    _ebisAllRows = _ebisAllRows ?? []
    return _ebisAllRows
  }
}

// When multiple tasks share the same taskNo (e.g. same inspection on Engine 1/2/3),
// make the description unique by substituting or prepending the ma (major assembly) label.
// Cases:
// - desc has generic location prefix (e.g. "Engine - …") and ma is more specific
//   (e.g. "Engine 1") → replace generic prefix with ma: "Engine 1 - Each 150 hr…"
// - desc has no location prefix → prepend ma: "Engine 1 - Each 150 hr…"
// - (taskNo + position + description) already unique → position disambiguates; skip
const _LOCATION_PREFIX_RE = /^(no\.?\s*\d+|fwd|aft|l\.?h\.?|r\.?h\.?|left\b|right\b|inbd|outbd|#\d+|engine|prop|airframe|apu)\b/i
function withComponentPrefixes(items) {
  const numCount  = {}
  const fullCount = {}
  for (const item of items) {
    const numKey  = (item['taskNo'] || '').toLowerCase().trim()
    const fullKey = [numKey, (item['position'] || '').toLowerCase().trim(), (item['description'] || '').toLowerCase().trim()].join('\x00')
    if (numKey) {
      numCount[numKey]   = (numCount[numKey]   || 0) + 1
      fullCount[fullKey] = (fullCount[fullKey] || 0) + 1
    }
  }
  return items.map(item => {
    const key     = (item['taskNo'] || '').toLowerCase().trim()
    const desc    = (item['description'] || '')
    const ma      = (item['ma'] || '').trim()
    const pos     = (item['position'] || '').trim()
    const fullKey = [key, pos.toLowerCase(), desc.toLowerCase()].join('\x00')

    if (numCount[key] <= 1 || !ma)  return item  // not a dup or no assembly label
    if (fullCount[fullKey] <= 1)    return item  // position already uniquely identifies it

    // If description starts with "Word - rest" and ma extends that word (e.g. ma="Engine 1"
    // extends desc prefix "Engine"), substitute the generic prefix with the specific ma label.
    const locMatch = desc.match(/^(.+?)\s*[-–—]\s*(.+)/s)
    if (locMatch && _LOCATION_PREFIX_RE.test(desc)) {
      const existingPrefix = locMatch[1].trim()
      const restOfDesc     = locMatch[2].trim()
      if (ma.toLowerCase().startsWith(existingPrefix.toLowerCase()) && ma.length > existingPrefix.length) {
        return { ...item, description: `${ma} - ${restOfDesc}` }
      }
      return item  // has a dash-separated location prefix but ma doesn't extend it — leave as-is
    }

    // Description has no dash-separated location prefix (or location word is part of the task
    // name, e.g. "Engine Oil Filter Restoration") — prepend ma directly.
    return { ...item, description: `${ma} - ${desc}` }
  })
}

// Composite key that uniquely identifies one task interval across all dimensions.
// registration + taskNo + cwDate pins a specific interval; ma + taskLocation distinguish
// position-specific tasks (LH/RH, Engine 1/2, AFT/FWD, etc.) with the same task number.
export function buildItemKey(registration, item) {
  return [
    (registration  || '').trim().toUpperCase(),
    (item['taskNo']       || '').trim(),
    (item['cw Date']      || '').trim(),
    (item['ma']           || '').trim(),
    (item['position']     || '').trim(),
  ].join('\x00')
}

// WO/item statuses that indicate completed work — used both at module level and in DueListItem.
const FINISHED_STATUSES_GLOBAL = /^(finished|bill sent\/completed|completed)$/i
// "Continued" means the item was carried over from a prior WO but not completed in it — treat as unplanned.
const CONTINUED_STATUS = /^continued$/i

// Parse an EBIS datetime string ("YYYY-MM-DD HH:MM:SS" or "YYYY-MM-DDTHH:MM:SS") to a local Date.
// Treats bare "YYYY-MM-DD" as local midnight so same-day comparisons with parseTraxxallDate work.
function parseEbisDate(s) {
  if (!s) return null
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!iso) return null
  // Include time component if present so the date is accurate, but keep it local (not UTC).
  const timePart = s.slice(10).trim().replace(/^T/, '')
  const d = timePart
    ? new Date(`${iso[1]}-${iso[2]}-${iso[3]}T${timePart}`)
    : new Date(parseInt(iso[1]), parseInt(iso[2]) - 1, parseInt(iso[3]))
  return isNaN(d.getTime()) ? null : d
}

// Compare two Dates at day granularity (strips time).
function dayMs(d) {
  return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())
}

// Extract the most recent signoff date across all signoff types (Qc, Pm, SafetyCheck, Sb).
function extractSignoffDate(signoffs) {
  if (!signoffs) return null
  const candidates = []
  for (const s of [signoffs.Qc, signoffs.Pm, signoffs.SafetyCheck, signoffs.Sb]) {
    if (!s) continue
    const d = parseEbisDate(s.SignoffDate ?? s.Date ?? s.DateTime ?? s.SignedDate ?? null)
    if (d) candidates.push(d)
  }
  return candidates.length ? new Date(Math.max(...candidates.map(d => d.getTime()))) : null
}

// Build discrepancy search items from a cached WO detail payload.
// Date priority for the compliance check in DueListItem:
//   1. lastServiceLogDate — latest actual technician hour entry (most reliable)
//   2. woSignoffDate      — WO formally signed off; good proxy for items with no service logs
//   3. woCreatedDate      — confirms WO belongs to current vs previous compliance interval
export function buildWoDiscrepItems(payload, woNumber = null) {
  if (!payload?.items?.length) return []

  // Index latest actual-work date per ItemNum from two sources:
  //   - ServiceLogs LoginDateTime/LogoutDateTime (clock-in/out entries)
  //   - Service[].LastWorkedDateTime (per-technician-assignment last work date)
  // Both come from IncludeService + IncludeServiceLogs and reflect real work activity,
  // unlike LastLogDateTimeUtc which is also bumped by admin edits.
  const serviceLogDates = {}
  const updateDate = (num, val) => {
    const d = parseEbisDate(val || null)
    if (d && (!serviceLogDates[num] || d > serviceLogDates[num])) serviceLogDates[num] = d
  }
  for (const log of (payload.serviceLogs || [])) {
    if (!log.ItemNum) continue
    updateDate(log.ItemNum, log.LoginDateTime)
    updateDate(log.ItemNum, log.LogoutDateTime)
  }
  for (const item of (payload.items || [])) {
    for (const svc of (item.Service || [])) {
      updateDate(item.ItemNum, svc.LastWorkedDateTime)
    }
  }

  const woSignoffDate  = extractSignoffDate(payload.wo?.Signoffs)
  const woCreatedDate  = parseEbisDate(payload.wo?.WoCreatedDate || payload.wo?.DateCreated || null)

  const woStatus = (payload.wo?.WoStatus || '').trim()
  return payload.items
    .map(item => ({
      text:               `${item.ItemDiscrep || ''} ${item.ItemCorrAction || ''}`.toLowerCase().trim(),
      status:             item.ItemStatus || '',
      woStatus,
      source:             'wo',
      lastServiceLogDate: serviceLogDates[item.ItemNum] || null,
      woSignoffDate,
      woCreatedDate,
      lastLogDate:        parseEbisDate(item.LastLogDateTimeUtc || null),
      woNumber,
      itemNum:            item.ItemNum ?? null,
    }))
    .filter(d => d.text)
}

export function getCachedWoDiscrepItems(woNumber) {
  return _woItemsCache[woNumber] || null
}

const MONTHS = { Jan:0,Feb:1,Mar:2,Apr:3,May:4,Jun:5,Jul:6,Aug:7,Sep:8,Oct:9,Nov:10,Dec:11 }

export function parseTraxxallDate(str) {
  if (!str) return null
  const parts = str.split('-')
  if (parts.length !== 3) return null
  const [day, mon, year] = parts
  const m = MONTHS[mon]
  if (m === undefined) return null
  return new Date(parseInt(year), m, parseInt(day))
}

function fmtDate(str) {
  const d = parseTraxxallDate(str)
  if (!d) return ''
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// Strip trailing unit word from numeric strings ("8,272.6 Hrs HRS" → "8,272.6 Hrs", "5,975 Ldg" → "5,975").
// Leaves date strings ("31-Aug-2026") and plain numbers unchanged.
// Used for due/cw fields where we always want to strip the trailing unit token.
function stripUnit(str) {
  if (!str) return ''
  return str.replace(/\s+[A-Za-z]+\s*$/, '').trim()
}

// Traxxall appends a redundant abbreviated unit tag after the readable one
// (e.g. "26 Hrs HRS", "5.3 Months MOS", "386.3 ACH (+20) ACH").
// Only strip it when (a) the trailing word is a known abbreviation and
// (b) a readable identifier already exists earlier in the string — so
// "26 Hrs" stays "26 Hrs" (don't strip the only identifier).
const UNIT_ABBREVS = new Set(['HRS','HR','MOS','MON','CAL','LDG','LDGS','ENC','ENCS','PRC','ACH','ACHS','FLCY','DAYS'])
function stripDuplicateUnit(str) {
  if (!str) return ''
  const m = str.match(/^(.*)\s+([A-Za-z]+)\s*$/)
  if (!m) return str.trim()
  if (!UNIT_ABBREVS.has(m[2].toUpperCase())) return str.trim()
  const rest = m[1].trim()
  if (!/[A-Za-z]/.test(rest)) return str.trim()
  return rest
}

function fmtVal(n) {
  if (n == null) return ''
  return Number(n).toLocaleString('en-US', { maximumFractionDigits: 1 })
}

// Parse "DAYS: OVD,HRS: OVD,LDG: OVD" → [{uom:'Days', rem:'OVD'}, ...]
// Splits only on commas immediately followed by a letter so that thousands-separator
// commas inside numeric values (e.g. "HRS: 8,172.6 Hrs") are preserved.
function parseEarliestDue(str) {
  if (!str) return []
  return str.split(/,(?=[A-Za-z])/).map(s => {
    const i = s.indexOf(':')
    if (i < 0) return null
    const uom = s.slice(0, i).trim()
    return { uom: uom.charAt(0) + uom.slice(1).toLowerCase(), rem: s.slice(i + 1).trim() }
  }).filter(Boolean)
}

function getUnit(str) {
  if (!str) return ''
  const m = str.match(/\b([A-Za-z]+)\s*$/)
  return m ? m[1].toUpperCase() : ''
}

const UOM_NORM = {
  LDG: 'LDG', LDGS: 'LDG', LANDING: 'LDG', LANDINGS: 'LDG',
  HRS: 'HRS', HR: 'HRS', HOURS: 'HRS', HOUR: 'HRS',
  MOS: 'DAYS', MON: 'DAYS', CAL: 'DAYS', MONTHS: 'DAYS', MONTH: 'DAYS',
  ENC: 'ENC', ENCS: 'ENC', 'ENGINE CYCLES': 'ENC', 'ENGINE CYCLE': 'ENC',
  PRC: 'PRC', 'PROPELLER CYCLES': 'PRC', 'PROP CYCLES': 'PRC',
  ACH: 'ACH', ACHS: 'ACH', 'AIRFRAME CYCLES': 'ACH', 'AIRFRAME CYCLE': 'ACH',
  FLCY: 'FLCY', 'FLIGHT CYCLES': 'FLCY', 'FLIGHT CYCLE': 'FLCY',
  FLT: 'FLT', FLTS: 'FLT', FLIGHTS: 'FLT', FLIGHT: 'FLT',
}
const normUnit = u => UOM_NORM[u?.toUpperCase()] || u?.toUpperCase() || ''

// Build { assemblyKey: { HRS: x, LDG: y, ENC: z } } from parseActuals() output.
// Indexes by BOTH description and type so lookups succeed regardless of which name
// the due list uses (e.g. "Engine 1" vs "IO-360-L2A Engine" vs "Engine").
export function buildActualsMap(actuals) {
  const map = {}
  for (const assembly of actuals) {
    const keys = [...new Set([assembly.description, assembly.type].filter(Boolean))]
    for (const key of keys) {
      if (!map[key]) map[key] = {}
      for (const v of assembly.values) {
        const uom = normUnit(v.uom)
        if (uom && uom !== 'DAYS') map[key][uom] = v.value
      }
    }
  }
  return map
}

export function getDiscrepItemsForReg(allRows, reg) {
  if (!allRows || !reg) return []
  const regLower = reg.toLowerCase()
  return allRows
    .filter(r => (r.AcRegNum || '').toLowerCase() === regLower)
    .map(r => ({
      text:     `${r.Discrep || ''} ${r.CorrAction || ''}`.toLowerCase().trim(),
      status:   r.WoStatus || '',
      source:   'listing',
      woNumber: r.WorkOrder || null,
    }))
    .filter(d => d.text)
}

// Strip all non-alphanumeric characters for separator-agnostic matching
// e.g. "76-00-00-002" → "7600000002" matches "760000002" in discrepancy text
export const normalizeForMatch = s => s.replace(/[^a-z0-9]/g, '')

// Returns true if the item's task number matches any entry in discrepItems (i.e. it is
// already known to EBIS as planned / in-work / complete / review).  Uses the same
// matching algorithm as DueListItem so the two stay in sync.
function isMatchedInEbis(item, discrepItems) {
  if (!discrepItems.length) return false
  const taskNo = (item['taskNo'] || '').toLowerCase().trim()
  if (!taskNo) return false
  const taskNoNorm     = normalizeForMatch(taskNo)
  const taskNoStripped = taskNoNorm.replace(/^[a-z](?=[0-9])/, '')
  const taskNoStem     = taskNoNorm.replace(/[a-z]$/, '')
  return discrepItems.some(d => {
    // Items from finished/completed WOs belong to past compliance intervals — don't let
    // them block re-selection when the same recurring task comes due again.
    // For WO-detail items check the WO-level status (woStatus); for listing items check status.
    const effectiveStatus = d.source === 'wo' ? d.woStatus : d.status
    if (FINISHED_STATUSES_GLOBAL.test(effectiveStatus) || CONTINUED_STATUS.test(effectiveStatus)) return false
    const dt            = normalizeForMatch(d.text)
    // Strip "Chapter XX-XX-XX" citations before matching — a chapter reference in the
    // discrepancy body is not the same as that chapter being the task on this WO.
    const textNoChapter = d.text.replace(/chapter\s{0,4}[\w-]{4,20}/g, '')
    const dtNoChapter   = dt.replace(/chapter[0-9a-z]{4,20}/g, '')
    return textNoChapter.includes(taskNo) ||
      (taskNoNorm.length >= 4 && dtNoChapter.includes(taskNoNorm)) ||
      (taskNoStripped !== taskNoNorm && taskNoStripped.length >= 4 && dtNoChapter.includes(taskNoStripped)) ||
      (taskNoStem !== taskNoNorm && taskNoStem.length >= 6 && dtNoChapter.includes(taskNoStem))
  })
}

// Compute plan status for a due list item against EBIS discrepancies.
// Mirrors DueListItem's status logic so calendar and due list views stay in sync.
// Returns 'complete' | 'review' | 'in-work' | 'planned' | 'unplanned'.
export function computeItemPlanStatus(item, discrepItems, overrideEntry = null, isInWork = false) {
  if (overrideEntry?.override_status) {
    const s = overrideEntry.override_status
    return s === 'planned' && isInWork ? 'in-work' : s
  }
  const taskNo = (item['taskNo'] || '').toLowerCase().trim()
  if (!taskNo || !discrepItems.length) return 'unplanned'
  const taskNoNorm     = normalizeForMatch(taskNo)
  const taskNoStripped = taskNoNorm.replace(/^[a-z](?=[0-9])/, '')
  const taskNoStem     = taskNoNorm.replace(/[a-z]$/, '')
  const matched = discrepItems.filter(d => {
    const dt            = normalizeForMatch(d.text)
    const textNoChapter = d.text.replace(/chapter\s{0,4}[\w-]{4,20}/g, '')
    const dtNoChapter   = dt.replace(/chapter[0-9a-z]{4,20}/g, '')
    return textNoChapter.includes(taskNo) ||
      (taskNoNorm.length >= 4 && dtNoChapter.includes(taskNoNorm)) ||
      (taskNoStripped !== taskNoNorm && taskNoStripped.length >= 4 && dtNoChapter.includes(taskNoStripped)) ||
      (taskNoStem !== taskNoNorm && taskNoStem.length >= 6 && dtNoChapter.includes(taskNoStem))
  })
  const cwDate         = parseTraxxallDate(item['cw Date'])
  const woMatched      = matched.filter(d => d.source === 'wo')
  const listingMatched = matched.filter(d => d.source === 'listing' && !CONTINUED_STATUS.test(d.status))
  const currentWoMatches = woMatched.filter(d => {
    if (CONTINUED_STATUS.test(d.status)) return false
    if (!FINISHED_STATUSES_GLOBAL.test(d.status)) return true
    if (!cwDate) return true
    const refDate = d.lastServiceLogDate ?? d.woSignoffDate
    if (refDate) return dayMs(refDate) >= dayMs(cwDate)
    if (d.woCreatedDate) return dayMs(d.woCreatedDate) >= dayMs(cwDate)
    return true
  })
  const openCurrents      = currentWoMatches.filter(d => !FINISHED_STATUSES_GLOBAL.test(d.status))
  const reliableFinished  = currentWoMatches.filter(d =>
    FINISHED_STATUSES_GLOBAL.test(d.status) && (
      d.lastServiceLogDate || d.woSignoffDate ||
      (d.woCreatedDate && cwDate && dayMs(d.woCreatedDate) >= dayMs(cwDate))
    )
  )
  const ambiguousFinished = currentWoMatches.filter(d =>
    FINISHED_STATUSES_GLOBAL.test(d.status) && !d.lastServiceLogDate && !d.woSignoffDate &&
    !(d.woCreatedDate && cwDate && dayMs(d.woCreatedDate) >= dayMs(cwDate))
  )
  let isPlanned, isComplete, isReview
  if (woMatched.length > 0) {
    isPlanned  = currentWoMatches.length > 0
    isComplete = reliableFinished.length > 0
    isReview   = isPlanned && !isComplete && ambiguousFinished.length > 0 && openCurrents.length === 0
  } else {
    isPlanned  = listingMatched.length > 0
    isComplete = false
    isReview   = false
  }
  const status = isComplete ? 'complete' : isReview ? 'review' : isPlanned ? 'planned' : 'unplanned'
  return status === 'planned' && isInWork ? 'in-work' : status
}

// Find actuals for a given major assembly name with fallback fuzzy matching.
// Tries: exact → case-insensitive exact → substring (handles "Engine 1" ↔ "Engine").
// Only falls back to Airframe actuals when ma is explicitly empty/null.
function findActuals(ma, actualsMap) {
  if (!ma) return actualsMap['Airframe'] || {}
  if (actualsMap[ma]) return actualsMap[ma]
  const maLow = ma.toLowerCase()
  for (const key of Object.keys(actualsMap)) {
    if (key.toLowerCase() === maLow) return actualsMap[key]
  }
  for (const key of Object.keys(actualsMap)) {
    const kLow = key.toLowerCase()
    if (maLow.includes(kLow) || kLow.includes(maLow)) return actualsMap[key]
  }
  return {}
}

// Compute remaining calendar time from today to a due date string (DD-Mon-YYYY).
// Uses day-level UTC comparison so "today → 4 days from now" = exactly 4 days,
// regardless of the current time of day.
function computeCalendarRem(dueDateStr) {
  const due = parseTraxxallDate(dueDateStr)
  if (!due) return ''
  const now     = new Date()
  const todayMs = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
  const dueMs   = Date.UTC(due.getFullYear(), due.getMonth(), due.getDate())
  const diffMs  = dueMs - todayMs
  if (diffMs <= 0) return 'OVD'
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24))
  if (diffDays < 60) return `${diffDays} days`
  const diffMonths = diffMs / (1000 * 60 * 60 * 24 * 30.44)
  return `${diffMonths.toFixed(1)} months`
}

// Return diff in days between due date and today (negative = overdue).
function computeCalendarDiff(dueDateStr) {
  const due = parseTraxxallDate(dueDateStr)
  if (!due) return null
  const now     = new Date()
  const todayMs = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
  const dueMs   = Date.UTC(due.getFullYear(), due.getMonth(), due.getDate())
  return Math.round((dueMs - todayMs) / (1000 * 60 * 60 * 24))
}

// Parse a calendar tolerance string to days ("30 Days" → 30, "2 Months" → 61, "2 Mos" → 61).
function parseTolDays(tolStr) {
  if (!tolStr) return null
  const num = parseFloat(String(tolStr).replace(/,/g, ''))
  if (isNaN(num)) return null
  if (/month|\bmos\b|\bmon\b|\bcal\b/i.test(tolStr)) return Math.round(num * 30.44)
  if (/year/i.test(tolStr))  return Math.round(num * 365.25)
  return Math.round(num)
}

// Parse a numeric tolerance string for hours/cycles ("40 Hrs" → 40, "5 Ldg" → 5).
function parseTolNum(tolStr) {
  if (!tolStr) return null
  const num = parseFloat(stripUnit(String(tolStr)).replace(/,/g, ''))
  return isNaN(num) ? null : num
}

// Extract only the segment of a combined string (e.g. "15-Aug-2025, 8,172.6 Hrs")
// that belongs to the given normalized unit key.
// Splits on "," then re-merges fragments where the previous accumulated segment is
// purely numeric — this preserves thousands-separator commas ("8,172.6") while still
// splitting on field-separator commas between distinct UoM values.
function extractForUnit(str, unitKey) {
  if (!str) return ''
  const rawParts = str.split(',')
  const parts = []
  for (const raw of rawParts) {
    const t = raw.trim()
    const prev = parts.length > 0 ? parts[parts.length - 1] : null
    if (prev !== null && /^[\d.,]+$/.test(prev)) {
      parts[parts.length - 1] = prev + ',' + t
    } else {
      parts.push(t)
    }
  }
  for (const part of parts) {
    if (normUnit(getUnit(part)) === unitKey) return part
  }
  return ''
}


// Parse item['remaining Time'] ("3.5 Months, 100 Hrs") into per-UoM map.
// NOTE: item['earliest Due'] is an aircraft-level field, not item-level — do not use it for rem values.
function parseRemainingTime(str) {
  if (!str) return { remMap: {}, simpleOvd: false }
  const trimmed = str.trim()
  if (/^OVD$/i.test(trimmed)) return { remMap: {}, simpleOvd: true }
  const segments = trimmed.split(/,(?=[A-Za-z\s])/)
  const remMap = {}
  for (const seg of segments.map(s => s.trim())) {
    if (!seg) continue
    const clean = stripDuplicateUnit(seg)
    if (/\bmonth/i.test(seg))      remMap['DAYS'] = clean
    else if (/\bday/i.test(seg))   remMap['DAYS'] = clean
    else if (/\bhr/i.test(seg))    remMap['HRS']  = clean
    else if (/\bldg/i.test(seg))   remMap['LDG']  = clean
    else if (/\benc/i.test(seg))   remMap['ENC']  = clean
    else if (/\bprc/i.test(seg))   remMap['PRC']  = clean
    else if (/\bach\b/i.test(seg)) remMap['ACH']  = clean
    else if (/\bflcy\b/i.test(seg)) remMap['FLCY'] = clean
    else if (/^OVD/i.test(seg))    remMap['DAYS'] = clean  // calendar OVD in multi-segment string
  }
  return { remMap, simpleOvd: false }
}

// Strip a leading recurrence/threshold prefix ("1. RE ", "2 T ", "RE ", "T ", etc.)
// then stripUnit.  Applied to every unit row so the REQ cell shows just the bare number.
function extractReqVal(segment) {
  if (!segment) return ''
  return stripUnit(segment.replace(/^[\d\s.]*(?:RE|T)\s+/i, ''))
}

// Parse a Traxxall calendar requirement that may carry a recurrence/threshold prefix
// like "1. RE 24 Mos", "2. T 12 Mos", or plain "24 Mos".
// Returns reLabel ("RE" or "T" when present) for the top approx row, and the bare
// numeric interval for the calendar row.
function parseCalReq(str) {
  if (!str) return { reLabel: '', numValue: '' }
  const m        = str.match(/^[\d\s.]*\b(RE|T)\b/i)
  const reLabel  = m ? m[1].toUpperCase() : ''
  const calSegment = extractForUnit(str, 'DAYS')
  return { reLabel, numValue: extractReqVal(calSegment) }
}

// Fixed row structure: approx date (top) + Mos/Yrs + Hrs + Ldg/Enc.
// Values routed strictly by unit — dates to Mos, hours to Hrs, cycles to Ldg/Enc.
export function buildUomRows(item, actualsMap = {}) {
  const { remMap, simpleOvd } = parseRemainingTime(item['remaining Time'] || '')
  const ma        = item['ma'] || 'Airframe'
  // Use fuzzy lookup so "Engine 1" correctly finds "Engine" / "IO-360-L2A Engine" actuals
  // rather than accidentally falling back to Airframe hours.
  const maActuals = findActuals(item['ma'], actualsMap)

  const reqUnit  = normUnit(getUnit(item['requirement']))
  const calLabel = /year/i.test(remMap['DAYS'] || '') ? 'Yrs' : 'Mos'
  const { reLabel: calReLabel, numValue: calReqVal } = parseCalReq(item['requirement'])
  // Extract per-unit tolerance from the active tolerance field. Traxxall may return a single
  // value ("30 Hrs") or a combined string for dual-limit tasks ("2 Months, 30 Hrs"). Use
  // extractForUnit so each row gets only the tolerance that matches its own unit.
  const tolStr = item['active Tolerance Window - Late'] || ''

  const rows = []

  // Always-top row: use the sooner of calendar hard due and Traxxall's estimated due date.
  // SchedAero projection may further override this in DueListItem rendering.
  const rawCal = item['next Due Date'] || ''
  const rawEst = item['estimated Due Date'] || ''
  let approxSrc = rawCal || rawEst
  if (rawCal && rawEst) {
    const calDate = parseTraxxallDate(rawCal)
    const estDate = parseTraxxallDate(rawEst)
    if (calDate && estDate && estDate < calDate) approxSrc = rawEst
  }
  const approxLabel   = approxSrc ? '≈ ' + fmtDate(approxSrc) : ''
  // isCalDue: true only when the approx date comes from a hard calendar due (next Due Date),
  // not just Traxxall's utilization estimate. soft-overdue should only trigger for hard calendar dates.
  const approxIsCalDue = !!rawCal
  if (approxLabel) {
    rows.push({ uom: '', cw: '', req: calReLabel, adj: '', tol: '', due: '', rem: approxLabel, isApprox: true, approxDate: approxSrc, isCalDue: approxIsCalDue })
  }

  // Calendar row (Mos / Yrs) — dates only
  const calDue = item['next Due Date'] || ''  // keep DD-Mon-YYYY as-is
  if (item['cw Date'] || calDue || remMap['DAYS']) {
    const calTolRaw = stripDuplicateUnit(extractForUnit(tolStr, 'DAYS'))
    // Resolve percentage-based calendar tolerances (e.g. "+3%m" = 3% of the months interval).
    // extractForUnit misses bare "%m" (unit "m" not in UOM_NORM), and parseTolDays would
    // misread a matched "%Mos" string as absolute months rather than a percentage.
    const calTol = (() => {
      const src = calTolRaw || tolStr
      const pm  = src.match(/[+-]?([\d.]+)\s*%\s*m/i)
      if (!pm) return calTolRaw
      const pct = parseFloat(pm[1])
      if (isNaN(pct)) return calTolRaw
      const reqSeg = extractForUnit(item['requirement'] || '', 'DAYS')
      const intervalMos = parseFloat(stripUnit(reqSeg).replace(/,/g, ''))
      if (isNaN(intervalMos) || intervalMos <= 0) return calTolRaw
      return `${Math.round(pct / 100 * intervalMos * 30.44)} days`
    })()
    // Normalize all month/year tolerances to days for consistent display
    const calTolFmt = (() => {
      if (!calTol) return calTol
      if (/month|\bmos\b|\bmon\b/i.test(calTol)) {
        const num = parseFloat(calTol)
        return isNaN(num) ? calTol : `${Math.round(num * 30.44)} days`
      }
      if (/year/i.test(calTol)) {
        const num = parseFloat(calTol)
        return isNaN(num) ? calTol : `${Math.round(num * 365.25)} days`
      }
      return calTol
    })()
    const calRemRaw = calDue ? computeCalendarRem(calDue) : (remMap['DAYS'] || '')
    let calRem    = calRemRaw
    let calTolRem = null
    if (calRemRaw === 'OVD' && calTolFmt && calDue) {
      const diffDays = computeCalendarDiff(calDue)   // negative when overdue
      const tolDays  = parseTolDays(calTolFmt)
      if (diffDays != null && tolDays != null) {
        const remaining = tolDays + diffDays
        if (remaining > 0) {
          calRem    = '0 days'
          calTolRem = `${remaining} days`
        }
        // else calRem stays 'OVD' — past the tolerance window
      }
      // else calRem stays 'OVD' — can't parse tolerance
    }
    rows.push({
      uom: calLabel,
      cw:  item['cw Date'] || '',
      req: calReqVal,
      adj: '',
      tol: calTolFmt,
      due: calDue,
      rem: calDue ? calRem : '',
      tolRem: calTolRem,
    })
  }

  // Hours row
  const hrsCw     = extractForUnit(item['cw Values'], 'HRS')
  const hrsDueRaw = extractForUnit(item['next Due'], 'HRS')
  if (remMap['HRS'] || hrsCw || hrsDueRaw) {
    const hrsTol = stripDuplicateUnit(extractForUnit(tolStr, 'HRS'))
    let hrsRem
    let hrsTolRem = null
    const hrsDueNum = hrsDueRaw ? parseFloat(stripUnit(hrsDueRaw).replace(/,/g, '')) : null
    if (hrsDueNum != null && maActuals['HRS'] != null) {
      const diff = hrsDueNum - maActuals['HRS']
      if (diff > 0) {
        hrsRem = fmtVal(diff) + ' Hrs'
      } else if (hrsTol) {
        const tolNum = parseTolNum(hrsTol)
        if (tolNum != null) {
          const tolRem = tolNum + diff  // diff is negative
          if (tolRem > 0) {
            hrsRem    = '0 Hrs'
            hrsTolRem = fmtVal(tolRem) + ' Hrs'
          } else {
            hrsRem = 'OVD'
          }
        } else {
          hrsRem = 'OVD'
        }
      } else {
        hrsRem = 'OVD'
      }
    } else {
      hrsRem = remMap['HRS'] || (simpleOvd ? 'OVD' : '')
    }
    rows.push({
      uom: 'Hrs',
      cw:  stripUnit(hrsCw),
      req: extractReqVal(extractForUnit(item['requirement'], 'HRS')),
      adj: '',
      tol: hrsTol,
      due: hrsDueRaw && hrsDueRaw !== hrsRem ? stripUnit(hrsDueRaw) : '',
      rem: hrsRem || '',
      tolRem: hrsTolRem,
    })
  }

  // Cycle rows (Ldg / Enc / Prc / Ach) — render one row per cycle type that has data
  const CYCLE_TYPES = [
    { key: 'LDG', label: 'Ldg' },
    { key: 'ENC', label: 'Enc' },
    { key: 'PRC', label: 'Prc' },
    { key: 'ACH',  label: 'Ach'  },
    { key: 'FLCY', label: 'Flcy' },
    { key: 'FLT',  label: 'Flt'  },
  ]
  for (const { key: ck, label: cl } of CYCLE_TYPES) {
    const cycleCw     = extractForUnit(item['cw Values'], ck)
    const cycleDueRaw = extractForUnit(item['next Due'], ck)
    if (remMap[ck] || cycleCw || cycleDueRaw) {
      const cycleTol = stripDuplicateUnit(extractForUnit(tolStr, ck))
      let cycleRem
      let cycleTolRem = null
      const cycleDueNum = cycleDueRaw ? parseFloat(stripUnit(cycleDueRaw).replace(/,/g, '')) : null
      if (cycleDueNum != null && maActuals[ck] != null) {
        const diff = cycleDueNum - maActuals[ck]
        if (diff > 0) {
          cycleRem = fmtVal(diff) + ' ' + cl
        } else if (cycleTol) {
          const tolNum = parseTolNum(cycleTol)
          if (tolNum != null) {
            const tolRem = tolNum + diff  // diff is negative
            if (tolRem > 0) {
              cycleRem    = '0 ' + cl
              cycleTolRem = fmtVal(tolRem) + ' ' + cl
            } else {
              cycleRem = 'OVD'
            }
          } else {
            cycleRem = 'OVD'
          }
        } else {
          cycleRem = 'OVD'
        }
      } else {
        cycleRem = remMap[ck] || (simpleOvd ? 'OVD' : '')
      }
      rows.push({
        uom: cl,
        cw:  stripUnit(cycleCw),
        req: extractReqVal(extractForUnit(item['requirement'], ck)),
        adj: '',
        tol: cycleTol,
        due: cycleDueRaw && cycleDueRaw !== cycleRem ? stripUnit(cycleDueRaw) : '',
        rem: cycleRem || '',
        tolRem: cycleTolRem,
      })
    }
  }

  // Fallback if nothing was built
  if (rows.filter(r => !r.isApprox).length === 0) {
    rows.push({
      uom: getUnit(item['next Due']) || getUnit(item['cw Values']) || '',
      cw:  item['cw Values'] || fmtDate(item['cw Date']) || '',
      req: item['requirement'] || '',
      adj: '',
      tol: stripDuplicateUnit(item['active Tolerance Window - Late'] || ''),
      due: stripUnit(item['next Due'] || ''),
      rem: stripDuplicateUnit(item['remaining Time'] || ''),
    })
  }

  return rows
}

// Tasks where the LATER limit governs (AND logic): calendar date alone does not make it due.
// Only the hours/cycles limit triggers the task. Key: taskNo → Set of regs where this applies.
export const AND_LOGIC_TASKS = new Map([
  ['2014-15-15', new Set(['N400FF'])],  // Horizontal Stabilizer Rib Assemblies
])
export function isAndLogicTask(taskNo, reg) {
  return AND_LOGIC_TASKS.get(taskNo || '')?.has(reg || '') ?? false
}
// Strip calendar dimension rows and clear isCalDue on the approx row so urgencyFromRows
// is driven solely by hours/cycles for AND-logic tasks.
export function stripCalendarForAndLogic(rows, taskNo, reg) {
  if (!isAndLogicTask(taskNo, reg)) return rows
  return rows
    .filter(r => !CAL_ROW_LABELS.has(r.uom))
    .map(r => r.isApprox ? { ...r, isCalDue: false } : r)
}

const HARD_UNITS = ['HRS', 'LDG', 'ENC', 'PRC', 'ACH', 'FLCY']

// Urgency based solely on item['remaining Time'] — earliest Due is aircraft-level, not item-level.
// NOTE: used only as a fallback. Prefer urgencyFromRows() when computed rows are available.
function urgencyLevel(item) {
  const { remMap, simpleOvd } = parseRemainingTime(item['remaining Time'] || '')
  if (simpleOvd) return 'overdue'
  if (HARD_UNITS.some(u => /^OVD/i.test((remMap[u] || '').trim()))) return 'overdue'
  if (/^OVD/i.test((remMap['DAYS'] || '').trim())) return 'warning'
  const daysRem = remMap['DAYS'] || ''
  const months = daysRem.match(/^([\d.,]+)\s*months?/i)
  if (months && parseFloat(months[1].replace(',', '')) <= 1) return 'warning'
  const days = daysRem.match(/^([\d.,]+)\s*days?/i)
  if (days && parseFloat(days[1].replace(',', '')) <= 30) return 'warning'
  return 'ok'
}

// Derive urgency from already-computed UoM rows (live actuals-based values).
// This is the authoritative source — used for both row background and badge counts.
// Urgency levels (highest to lowest): 'overdue' > 'soft-overdue' > 'warning' > 'ok'
// soft-overdue: estimated date passed but hours still remain (>30); operator keeps flying until hrs run out.
// warning: ≤30 hrs remaining or calendar within 30 days (start scheduling window).
const HARD_ROW_LABELS = new Set(['Hrs', 'Ldg', 'Enc', 'Prc', 'Ach', 'Flcy'])
const CAL_ROW_LABELS  = new Set(['Mos', 'Yrs'])
export function urgencyFromRows(rows) {
  let level          = 'ok'
  let estDatePassed  = false
  let anyHrsRemaining = false

  for (const r of rows) {
    if (r.isApprox) {
      if (r.approxDate) {
        const now     = new Date()
        const todayMs = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
        const due     = parseTraxxallDate(r.approxDate)
        if (due) {
          const dueMs    = Date.UTC(due.getFullYear(), due.getMonth(), due.getDate())
          const diffDays = Math.round((dueMs - todayMs) / (1000 * 60 * 60 * 24))
          if (diffDays < 0) {
            // Only treat a past approx date as soft-overdue when it comes from a hard
            // calendar due date (next Due Date). A past Traxxall utilization estimate
            // just means the aircraft flew slower than expected — the item still has
            // hours remaining and should stay warning, not soft-overdue.
            if (r.isCalDue) estDatePassed = true
          } else if (diffDays <= 30 && level === 'ok' && r.isCalDue) {
            // Only promote to warning when the approx date comes from a hard calendar
            // due date. Traxxall/SchedAero estimated dates may disagree with our fleet
            // utilization projection — let the individual UoM rows (Hrs, Enc, etc.)
            // drive urgency for hours-only tasks so the sort rank matches the display.
            level = 'warning'
          }
        }
      }
      continue
    }
    if (!r.rem) continue
    if (/^deficient$/i.test(r.rem)) return 'deficient'
    if (/^OVD/i.test(r.rem)) {
      if (HARD_ROW_LABELS.has(r.uom) || CAL_ROW_LABELS.has(r.uom)) return 'overdue'
    } else if (/^0\s/.test(r.rem) && r.tol) {
      if (level !== 'overdue') level = 'warning'
    } else {
      if (r.uom === 'Hrs') {
        const hrsNum = parseFloat(String(r.rem).replace(/,/g, ''))
        if (!isNaN(hrsNum) && hrsNum > 0) {
          anyHrsRemaining = true
          if (hrsNum <= 30 && level === 'ok') level = 'warning'
        }
      } else if (level === 'ok') {
        const months = r.rem.match(/^([\d.,]+)\s*months?/i)
        if (months && parseFloat(months[1].replace(',', '')) <= 1) level = 'warning'
        const days = r.rem.match(/^(\d+)\s*days?/i)
        if (days && parseInt(days[1]) <= 30) level = 'warning'
      }
    }
  }

  if (estDatePassed && level !== 'overdue') {
    return 'soft-overdue'
  }
  return level
}

// Whether an item falls into the "needs scheduled" bucket (matches FleetDueListPage thresholds).
// Uses effective due date: SchedAero projection → Traxxall estimated due → calendar hard due.
const NS_DAYS_DL = 30
function rowNeedsScheduled(projDate, estDue, calDue) {
  // Priority mirrors itemNeedsScheduled in FleetDueListPage:
  // use projDate + calDue when available; fall back to estDue only when projDate is absent.
  const primary = [projDate, calDue].filter(Boolean)
  const candidates = primary.length > 0 ? primary : [estDue].filter(Boolean)
  if (!candidates.length) return false
  const earliest = new Date(Math.min(...candidates.map(d => d.getTime())))
  earliest.setHours(0, 0, 0, 0)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return (earliest - today) / 86_400_000 <= NS_DAYS_DL
}

function sortPriority(item, projections, reg = '') {
  // AND-logic tasks ignore calDue — only hours govern the due date.
  const skipCal = isAndLogicTask(item['taskNo'], reg)
  const calDue  = skipCal ? null : parseTraxxallDate(item['next Due Date'])
  const estDue  = parseTraxxallDate(item['estimated Due Date'])
  const now     = new Date()
  // A past calDue means the calendar interval has already expired but hours remain
  // (soft-overdue). The urgency rank already surfaces these items above ok items;
  // using a past calDue as the sort anchor would incorrectly pin them to the very top
  // of the list ahead of items with nearer hours-based due dates. Only treat calDue
  // as a hard sort bound when it's still in the future.
  const futureCalDue = calDue && calDue > now ? calDue : null
  if (projections) {
    const projKey = [item['taskNo'] || '', item['cw Date'] || '', item['ma'] || '', item['position'] || ''].join('\x00')
    const projDate = projections[projKey]?.projDate || null
    if (projDate) {
      if (futureCalDue) return (projDate < futureCalDue ? projDate : futureCalDue).getTime()
      return projDate.getTime()
    }
  }
  const anchor = futureCalDue && estDue ? (estDue < futureCalDue ? estDue : futureCalDue) : (futureCalDue || estDue)
  return anchor ? anchor.getTime() : Infinity
}

const URGENCY_SORT_RANK = { deficient: 5, overdue: 4, 'soft-overdue': 3.5, 'tol-ovd': 3, warning: 2, ok: 1 }
function itemUrgencyRank(item, actualsMap, reg) {
  const rows = stripCalendarForAndLogic(buildUomRows(item, actualsMap), item['taskNo'], reg)
  if (rows.some(r => !r.isApprox && r.tolRem != null)) return URGENCY_SORT_RANK['tol-ovd']
  return URGENCY_SORT_RANK[urgencyFromRows(rows)] ?? 1
}

// Flatten actuals response into display-ready assembly rows (current only, default UoMs)
export function parseActuals(data) {
  if (!Array.isArray(data)) return []
  return data
    .filter(a => a.isCurrent)
    .map(a => {
      const vals = (a.values || []).filter(v => v.isDefaultUom)
      if (!vals.length) return null
      return {
        type:        a.majorAssemblyType || '',
        description: a.majorAssemblyDescription || a.majorAssemblyType || '',
        position:    a.majorAssemblyPosition,
        values:      vals.map(v => ({ uom: v.unitOfMeasure, value: v.value, asOf: v.asOfDate })),
        asOf:        vals[0]?.asOfDate || '',
      }
    })
    .filter(Boolean)
}

const AIRCRAFT_STATUS = {
  Red:    { dot: 'bg-red-500',    label: 'text-red-700',    pill: 'bg-red-100'    },
  Yellow: { dot: 'bg-yellow-400', label: 'text-yellow-700', pill: 'bg-yellow-100' },
  Green:  { dot: 'bg-green-500',  label: 'text-green-700',  pill: 'bg-green-100'  },
}

const TASK_TYPE_COLORS = {
  'Airworthiness Directive': 'bg-red-100 text-red-700',
  'Service Bulletin':        'bg-orange-100 text-orange-700',
  'Inspection':              'bg-blue-100 text-blue-700',
  'Package':                 'bg-purple-100 text-purple-700',
  'Part':                    'bg-gray-100 text-gray-600',
  'Snag':                    'bg-pink-100 text-pink-700',
  'Major Assembly':          'bg-indigo-100 text-indigo-700',
}

function TaskTypeBadge({ type }) {
  const cls = TASK_TYPE_COLORS[type] || 'bg-gray-100 text-gray-500'
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap ${cls}`}>
      {type || '—'}
    </span>
  )
}

const UOM_HEADERS  = ['UoM', 'C/W', 'Req', 'Adj', 'Tol', 'Due', 'Time.rem.']
const COL_WIDTHS   = { 'UoM': '8%', 'C/W': '14%', 'Req': '12%', 'Adj': '7%', 'Tol': '12%', 'Due': '16%', 'Time.rem.': '31%' }

const FINISHED_STATUSES = FINISHED_STATUSES_GLOBAL

function DueListItem({ item, actualsMap, discrepItems = [], overrideEntry = null, onSaveOverride, onClearOverride, isInWork = false, onTaskClick, projDate = null, reg = '' }) {
  const [showPopover, setShowPopover] = useState(false)
  const [noteText, setNoteText]       = useState('')
  const popoverRef = useRef(null)

  useEffect(() => {
    if (!showPopover) return
    const handle = (e) => { if (popoverRef.current && !popoverRef.current.contains(e.target)) setShowPopover(false) }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [showPopover])

  const baseRows = buildUomRows(item, actualsMap)
  // For AND-logic tasks, strip calendar rows so urgency is driven by hours only.
  const andLogic    = isAndLogicTask(item['taskNo'], reg)
  const effectiveBase = stripCalendarForAndLogic(baseRows, item['taskNo'], reg)
  const hasTolActive = effectiveBase.some(r => !r.isApprox && r.tolRem != null)

  const rows = (() => {
    if (hasTolActive) {
      // Item is already past its hard limit and living in the tolerance window.
      // The approx date must reflect WHEN it entered tolerance (a past date), not any
      // future calendar date.  Show calDue only when it's already in the past; suppress
      // it when the hard calendar limit hasn't been crossed yet (e.g. hours-tol-ovd with
      // a future calendar due date).
      const calDue = andLogic ? null : parseTraxxallDate(item['next Due Date'])
      const today  = new Date(); today.setHours(0, 0, 0, 0)
      return effectiveBase.map(r => {
        if (!r.isApprox) return r
        if (calDue && calDue < today) {
          const calFmt = calDue.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          return { ...r, rem: `≈ ${calFmt}`, isSchedAero: false }
        }
        return { ...r, rem: '' }  // no meaningful past date available — suppress
      })
    }
    if (projDate) {
      return effectiveBase.map(r => {
        if (!r.isApprox) return r
        // Priority: our projection wins over Traxxall estimated dates.
        // The only thing that can beat the projection is a firm calendar hard due date
        // that arrives sooner.  AND-logic tasks ignore calDue entirely.
        const calDue = andLogic ? null : parseTraxxallDate(item['next Due Date'])
        const effectiveDate = calDue && calDue < projDate ? calDue : projDate
        const effectiveFmt = effectiveDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        return { ...r, rem: `≈ ${effectiveFmt}`, isSchedAero: effectiveDate === projDate }
      })
    }
    return effectiveBase
  })()
  const level = urgencyFromRows(rows)
  const estDue        = parseTraxxallDate(item['estimated Due Date'])
  const calDue        = parseTraxxallDate(item['next Due Date'])
  const needsSchedule = !hasTolActive && level !== 'overdue' && level !== 'deficient'
    && rowNeedsScheduled(projDate, estDue, andLogic ? null : calDue)

  const rowBg      = level === 'deficient'                  ? 'bg-red-100'
                   : level === 'overdue'                    ? 'bg-red-50/50'
                   : (level === 'soft-overdue' || hasTolActive) ? 'bg-orange-50/50'
                   : needsSchedule                          ? 'bg-yellow-50/50'
                   : 'bg-white'
  const leftBorder = level === 'deficient'                  ? 'border-l-red-600'
                   : level === 'overdue'                    ? 'border-l-red-400'
                   : (level === 'soft-overdue' || hasTolActive) ? 'border-l-orange-400'
                   : needsSchedule                          ? 'border-l-yellow-400'
                   : 'border-l-gray-200'

  const taskNo         = (item['taskNo'] || '').toLowerCase().trim()
  const taskNoNorm     = normalizeForMatch(taskNo)
  // Some EBIS entries omit a leading letter prefix (e.g. "H340006" → "340006")
  const taskNoStripped = taskNoNorm.replace(/^[a-z](?=[0-9])/, '')
  // Some EBIS entries omit a trailing revision letter (e.g. "L60-CA001-01-400A" → "L60-CA001-01-400")
  const taskNoStem     = taskNoNorm.replace(/[a-z]$/, '')
  const hasEbis    = discrepItems.length > 0
  const matched    = hasEbis && !!taskNo
    ? discrepItems.filter(d => {
        const dt            = normalizeForMatch(d.text)
        const textNoChapter = d.text.replace(/chapter\s{0,4}[\w-]{4,20}/g, '')
        const dtNoChapter   = dt.replace(/chapter[0-9a-z]{4,20}/g, '')
        return textNoChapter.includes(taskNo) ||
          (taskNoNorm.length >= 4 && dtNoChapter.includes(taskNoNorm)) ||
          (taskNoStripped !== taskNoNorm && taskNoStripped.length >= 4 && dtNoChapter.includes(taskNoStripped)) ||
          (taskNoStem !== taskNoNorm && taskNoStem.length >= 6 && dtNoChapter.includes(taskNoStem))
      })
    : []
  // Split matches by source — WO-specific data takes full precedence over the listing fallback.
  const cwDate         = parseTraxxallDate(item['cw Date'])
  const woMatched      = matched.filter(d => d.source === 'wo')
  const listingMatched = matched.filter(d => d.source === 'listing' && !CONTINUED_STATUS.test(d.status))

  // A Finished item is "current" only if evidence of work exists strictly after the Traxxall
  // C/W date. Date priority:
  //   1. lastServiceLogDate — actual tech hour entries (ignores admin-only LastLogDateTimeUtc bumps)
  //   2. woSignoffDate      — WO formally closed; catches Finished items with no service logs
  //   3. woCreatedDate      — if WO was created on/before the C/W date it can only be the
  //                           previous compliance WO; if after, it is current interval work
  // NOTE: LastLogDateTimeUtc (lastLogDate) is intentionally excluded — it gets bumped by admin
  // edits and cannot reliably indicate when actual tech work occurred.
  const currentWoMatches = woMatched.filter(d => {
    if (CONTINUED_STATUS.test(d.status)) return false   // Continued → treat as unplanned
    if (!FINISHED_STATUSES.test(d.status)) return true  // open → always current
    if (!cwDate) return true
    const refDate = d.lastServiceLogDate ?? d.woSignoffDate
    if (refDate) return dayMs(refDate) >= dayMs(cwDate)
    if (d.woCreatedDate) return dayMs(d.woCreatedDate) >= dayMs(cwDate)
    return true  // truly no dates → keep (Review)
  })

  // Categorize current WO matches by how reliable their date evidence is.
  // woCreatedDate >= cwDate is treated as reliable: a Finished item on a WO that was opened
  // on or after the C/W date is current-interval work — no admin-edit ambiguity possible.
  const openCurrents      = currentWoMatches.filter(d => !FINISHED_STATUSES.test(d.status))
  const reliableFinished  = currentWoMatches.filter(d =>
    FINISHED_STATUSES.test(d.status) && (
      d.lastServiceLogDate ||
      d.woSignoffDate ||
      (d.woCreatedDate && cwDate && dayMs(d.woCreatedDate) >= dayMs(cwDate))
    )
  )
  const ambiguousFinished = currentWoMatches.filter(d =>
    FINISHED_STATUSES.test(d.status) && !d.lastServiceLogDate && !d.woSignoffDate &&
    !(d.woCreatedDate && cwDate && dayMs(d.woCreatedDate) >= dayMs(cwDate))
  )

  let isPlanned, isComplete, isReview
  if (woMatched.length > 0) {
    isPlanned  = currentWoMatches.length > 0
    isComplete = reliableFinished.length > 0
    isReview   = isPlanned && !isComplete && ambiguousFinished.length > 0 && openCurrents.length === 0
  } else {
    isPlanned  = listingMatched.length > 0
    isComplete = false
    isReview   = false
  }

  // Apply manual override when present — only Review items (or already-overridden items) are overridable.
  const computedStatus  = isComplete ? 'complete' : isReview ? 'review' : isPlanned ? 'planned' : 'unplanned'
  const effectiveStatus = overrideEntry?.override_status ?? computedStatus
  const canOverride     = computedStatus === 'review' || !!overrideEntry

  const showComplete  = effectiveStatus === 'complete'
  const showReview    = effectiveStatus === 'review'
  const showInWork    = effectiveStatus === 'planned' && isInWork
  const showPlanned   = effectiveStatus === 'planned' && !isInWork

  return (
    <div className={`border border-gray-200 rounded-lg ${rowBg}`}>
      <div className="flex min-w-0">
        {/* Left: task identification */}
        <div className={`flex-1 min-w-0 border-r border-gray-200 border-l-4 ${leftBorder} px-3 py-2.5 space-y-0.5`}>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span
              className={`text-xs font-bold text-brand-red ${onTaskClick && (showComplete || showInWork || showPlanned) ? 'cursor-pointer hover:underline' : ''}`}
              onClick={onTaskClick && (showComplete || showInWork || showPlanned) ? () => {
                const best = reliableFinished[0] || openCurrents[0] || woMatched[0] || listingMatched[0]
                if (best?.woNumber) onTaskClick({ workOrderNumber: best.woNumber, highlightItemNum: best.itemNum ?? null })
              } : undefined}
            >{item['taskNo'] || '—'}</span>
            <TaskTypeBadge type={item['task Type']} />
          </div>
          <div className="text-xs font-semibold text-gray-800 leading-snug">
            {item['position'] && (
              <span className="text-gray-500 font-medium">{item['position']} — </span>
            )}
            {item['description'] || '—'}
          </div>
          {(item['ma'] || item['ata']) && (
            <div className="text-xs text-gray-500">
              {[item['ma'], item['ata'] ? `ATA ${item['ata'].trim()}` : ''].filter(Boolean).join(' · ')}
            </div>
          )}
          {item['task Category'] && (
            <div className="text-xs text-gray-400 leading-snug">{item['task Category']}</div>
          )}
          {(item['position'] || item['zone'] || item['access']) && (
            <div className="text-xs text-gray-400 leading-snug">
              {[item['position'], item['zone'], item['access']].filter(Boolean).join(' · ')}
            </div>
          )}
          {item['pn'] && <div className="text-xs text-gray-400">{item['pn']}</div>}
          {item['sn'] && <div className="text-xs text-gray-400">{item['sn']}</div>}
        </div>

        {/* Middle: EBIS status indicator (Complete / Review / Planned / Unplanned) */}
        <div
          className={`flex-none w-[4.5rem] flex flex-col items-center justify-center border-r border-gray-200 px-2 py-2 gap-0.5 relative ${canOverride ? 'cursor-pointer' : ''}`}
          title={
            !hasEbis      ? 'EBIS data not loaded' :
            canOverride   ? (overrideEntry ? `Manually set · click to change` : `No technician time or signoff to confirm — click to set manually`) :
            `Task: ${item['taskNo'] || '—'} · checked against ${discrepItems.length} EBIS item(s)`
          }
          onClick={canOverride ? () => { setNoteText(overrideEntry?.notes || ''); setShowPopover(p => !p) } : undefined}
        >
          {!hasEbis ? (
            <span className="text-gray-300 text-xs">—</span>
          ) : showComplete ? (
            <>
              <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-[10px] font-semibold text-green-600 leading-none">Complete</span>
            </>
          ) : showReview ? (
            <>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-[10px] font-semibold text-gray-500 leading-none">Review</span>
            </>
          ) : showInWork ? (
            <>
              <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21.75 6.75a4.5 4.5 0 01-4.884 4.484c-1.076-.091-2.264.071-2.95.904l-7.152 8.684a2.548 2.548 0 11-3.586-3.586l8.684-7.152c.833-.686.995-1.874.904-2.95a4.5 4.5 0 016.336-4.486l-3.276 3.276a3.004 3.004 0 002.25 2.25l3.276-3.276c.256.565.398 1.192.398 1.852z" />
              </svg>
              <span className="text-[10px] font-semibold text-blue-600 leading-none">In Work</span>
            </>
          ) : showPlanned ? (
            <>
              <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-[10px] font-semibold text-yellow-600 leading-none">Planned</span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span className="text-[10px] font-semibold text-red-500 leading-none">Unplanned</span>
            </>
          )}
          {overrideEntry && (
            <span className="text-[8px] text-gray-400 leading-none mt-0.5">manual</span>
          )}

          {/* Override popover */}
          {canOverride && showPopover && (
            <div
              ref={popoverRef}
              className="absolute z-50 top-full left-1/2 -translate-x-1/2 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-xl p-2.5 space-y-2"
              onClick={e => e.stopPropagation()}
            >
              {overrideEntry && (
                <p className="text-[9px] text-gray-400 leading-snug">
                  Set {new Date(overrideEntry.overridden_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  {overrideEntry.notes ? ` · ${overrideEntry.notes}` : ''}
                </p>
              )}
              <p className="text-[9px] font-semibold text-gray-500 uppercase tracking-wide">Set status</p>
              <div className="flex flex-col gap-1">
                {[
                  { value: 'complete',  label: 'Complete',  cls: 'text-green-700 border-green-200 hover:bg-green-50' },
                  { value: 'planned',   label: 'Planned',   cls: 'text-yellow-700 border-yellow-200 hover:bg-yellow-50' },
                  { value: 'unplanned', label: 'Unplanned', cls: 'text-red-600 border-red-200 hover:bg-red-50' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    className={`text-[10px] font-semibold border rounded px-2 py-1 text-left transition-colors ${opt.cls} ${overrideEntry?.override_status === opt.value ? 'ring-1 ring-current' : ''}`}
                    onClick={() => { onSaveOverride(opt.value, noteText); setShowPopover(false) }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <input
                type="text"
                maxLength={80}
                className="w-full text-[10px] border border-gray-200 rounded px-1.5 py-1 text-gray-700 placeholder-gray-300 focus:outline-none focus:border-gray-400"
                placeholder="Note (optional)"
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
              />
              {overrideEntry && (
                <button
                  className="w-full text-[9px] text-gray-400 hover:text-red-500 text-center transition-colors"
                  onClick={() => { onClearOverride(); setShowPopover(false) }}
                >
                  Clear override
                </button>
              )}
            </div>
          )}
        </div>

        {/* Right: per-UoM data table */}
        <div className="flex-none w-[44rem]">
          <table className="w-full text-xs table-fixed">
            <colgroup>
              {UOM_HEADERS.map(h => (
                <col key={h} style={{ width: COL_WIDTHS[h] }} />
              ))}
            </colgroup>
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                {UOM_HEADERS.map(h => (
                  <th key={h} className="px-3 py-1.5 text-left font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className={i < rows.length - 1 ? 'border-b border-gray-100' : ''}>
                  <td className="px-3 py-1.5 font-semibold text-gray-600 whitespace-nowrap">{row.uom}</td>
                  <td className="px-3 py-1.5 text-gray-600 whitespace-nowrap">{row.cw}</td>
                  <td className="px-3 py-1.5 text-gray-600">{row.req}</td>
                  <td className="px-3 py-1.5 text-gray-500">{row.adj}</td>
                  <td className="px-3 py-1.5 text-gray-500 whitespace-nowrap">{row.tol}</td>
                  <td className="px-3 py-1.5 text-gray-600 whitespace-nowrap">{row.due}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap">
                    {!row.rem ? null
                      : row.rem.startsWith('≈')
                        ? <span className={`${row.isSchedAero ? 'text-teal-600 font-medium' : 'text-gray-400'} italic text-xs`}>{row.rem}</span>
                        : /^0\s/.test(row.rem) && (row.tolRem ?? row.tol)
                          ? <span className="text-red-500 font-semibold">{row.rem}<span className="font-normal"> (+{row.tolRem ?? row.tol})</span></span>
                          : row.rem === 'OVD' || row.rem.includes('OVD')
                            ? <span className="text-red-600 font-bold">{row.rem}</span>
                            : <span className="text-gray-700">{row.rem}{row.tol && !row.rem.includes('(+') ? <span className="text-gray-400"> (+{row.tol})</span> : null}</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function ActualsStrip({ actuals, loading }) {
  if (loading) {
    return (
      <div className="flex gap-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-8 w-40 bg-gray-100 rounded-lg animate-pulse" />
        ))}
      </div>
    )
  }
  if (!actuals.length) return null

  const assemblyGroup = (a) => {
    const t = (a.type || '').toLowerCase()
    const d = (a.description || '').toLowerCase()
    const pos = parseInt(a.position) || 0
    if (t === 'airframe' || d.includes('airframe')) return [0, 0]
    if (t === 'engine'   || d.includes('engine'))   return [1, pos]
    if (t === 'propeller'|| d.includes('prop'))      return [2, pos]
    return [3, pos]
  }
  const sorted = [...actuals].sort((a, b) => {
    const [ag, ap] = assemblyGroup(a)
    const [bg, bp] = assemblyGroup(b)
    return ag !== bg ? ag - bg : ap - bp
  })

  return (
    <div className="flex items-start gap-3 flex-wrap">
      {sorted.map((assembly, i) => (
        <div key={i} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5">
          <span className="text-xs font-semibold text-gray-500 whitespace-nowrap">{assembly.description}</span>
          <span className="text-gray-300 text-xs">|</span>
          <span className="text-xs text-gray-700 whitespace-nowrap">
            {assembly.values.map(v => `${fmtVal(v.value)} ${v.uom}`).join(' · ')}
          </span>
          {assembly.asOf && (
            <span className="text-xs text-gray-400 whitespace-nowrap">as of {assembly.asOf}</span>
          )}
        </div>
      ))}
    </div>
  )
}

export default function TraxxallDueList({ registration, altRegistration = '', workOrderNumbers = [], projectStatus = '', onTaskClick, onCountsUpdate, projections = {}, scheduleCount = null, ovdCount: ovdCountProp = null, tolCount: tolCountProp = null, overviewOnly = false, onAddToSchedule, hideCountBadges = false }) {
  const cached = _cache[registration]
  const [items, setItems]               = useState(() => cached?.items || [])
  const [loading, setLoading]           = useState(!cached)
  const [error, setError]               = useState(null)
  const [staleWarning, setStaleWarning] = useState(null)
  const [lastFetched, setLastFetched]   = useState(() => cached?.lastFetched || null)

  const [actuals, setActuals]               = useState(() => cached?.actuals || [])
  const [actualsLoading, setActualsLoading] = useState(false)
  const [fetchedLive, setFetchedLive]       = useState(false)

  const [search, setSearch]         = useState('')
  const [typeFilter, setTypeFilter] = useState('All')
  const [maFilter, setMaFilter]     = useState('All')
  const [sortDir, setSortDir]       = useState('asc')
  const [horizonDays, setHorizonDays] = useState(180)
  const [discrepItems, setDiscrepItems] = useState(() =>
    _ebisAllRows ? getDiscrepItemsForReg(_ebisAllRows, registration) : []
  )
  const [woDiscrepItems, setWoDiscrepItems] = useState(() => {
    if (!workOrderNumbers.length) return []
    const allCached = workOrderNumbers.every(wo => _woItemsCache[wo])
    if (!allCached) return []
    // Do not deduplicate across WOs — the same task may appear on both an old signed-off WO
    // (previous compliance) and a new open WO (current work), and DueListItem needs both entries.
    return workOrderNumbers.flatMap(wo => _woItemsCache[wo])
  })

  const [overridesMap, setOverridesMap] = useState(() => _overridesCache[registration] || {})

  const [selectedKeys, setSelectedKeys]         = useState(new Set())
  const [pushMode, setPushMode]                 = useState('new-wo')
  const [pushResult, setPushResult]             = useState(null) // { woNumber, regNum }
  const [existingWos, setExistingWos]           = useState([]) // { WorkOrder, WorkOrderDisplay, WoStatus }[]
  const [existingWosLoading, setExistingWosLoading] = useState(false)
  const [selectedExistingWo, setSelectedExistingWo] = useState('')
  const [manualWoEntry, setManualWoEntry]       = useState('')
  const [formatCopied, setFormatCopied]         = useState(false)
  const [pushPreviewItems, setPushPreviewItems] = useState(null) // null | array — opens EbisPushConfirmModal
  const [previewLoading, setPreviewLoading]     = useState(false)

  const authHeaders = {
    'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
    'Content-Type': 'application/json',
  }

  useEffect(() => {
    if (pushMode !== 'existing' || !registration) return
    setExistingWosLoading(true)
    setExistingWos([])
    setSelectedExistingWo('')
    setManualWoEntry('')

    const regs = new Set(
      [registration, altRegistration].filter(Boolean).map(r => r.trim().toUpperCase())
    )

    // WOs: filter the cached full listing client-side by reg.
    // Quotes are not exposed by the EBIS external API — use the manual entry field below.
    fetchEbisListing()
      .then(rows => {
        const seen = new Set()
        const wos = rows
          .filter(r => regs.has((r.AcRegNum || '').trim().toUpperCase()))
          .filter(r => { if (seen.has(r.WorkOrder)) return false; seen.add(r.WorkOrder); return true })
          .map(r => ({ WorkOrder: r.WorkOrder, WorkOrderDisplay: r.WorkOrderDisplay, WoStatus: r.WoStatus, label: r.WorkOrderDisplay || r.WorkOrder }))
        wos.sort((a, b) => (b.WorkOrder || '').localeCompare(a.WorkOrder || ''))
        setExistingWos(wos)
      })
      .catch(() => {})
      .finally(() => setExistingWosLoading(false))
  }, [pushMode, registration, altRegistration])

  const fetchOverrides = async (reg) => {
    if (!reg || !SUPABASE_URL) return
    if (_overridesCache[reg]) { setOverridesMap(_overridesCache[reg]); return }
    try {
      const r = await fetch(
        `${SUPABASE_URL}/rest/v1/due_list_overrides?registration=eq.${encodeURIComponent(reg)}&select=*`,
        { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` } }
      )
      const rows = r.ok ? await r.json() : []
      const map = {}
      for (const row of rows) {
        const key = [row.registration, row.task_no, row.cw_date, row.ma, row.task_location].join('\x00')
        map[key] = row
      }
      _overridesCache[reg] = map
      setOverridesMap(map)
    } catch {}
  }

  const saveOverride = async (key, status, notes) => {
    const [reg, task_no, cw_date, ma, task_location] = key.split('\x00')
    const entry = { registration: reg, task_no, cw_date, ma, task_location, override_status: status, notes: notes || null, overridden_at: new Date().toISOString() }
    try {
      await fetch(`${SUPABASE_URL}/rest/v1/due_list_overrides`, {
        method:  'POST',
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json', Prefer: 'resolution=merge-duplicates' },
        body:    JSON.stringify(entry),
      })
      const updated = { ...(_overridesCache[reg] || {}), [key]: entry }
      _overridesCache[reg] = updated
      setOverridesMap({ ...updated })
    } catch {}
  }

  const clearOverride = async (key) => {
    const [reg, task_no, cw_date, ma, task_location] = key.split('\x00')
    const q = `registration=eq.${encodeURIComponent(reg)}&task_no=eq.${encodeURIComponent(task_no)}&cw_date=eq.${encodeURIComponent(cw_date)}&ma=eq.${encodeURIComponent(ma)}&task_location=eq.${encodeURIComponent(task_location)}`
    try {
      await fetch(`${SUPABASE_URL}/rest/v1/due_list_overrides?${q}`, {
        method:  'DELETE',
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
      })
      const updated = { ...(_overridesCache[reg] || {}) }
      delete updated[key]
      _overridesCache[reg] = updated
      setOverridesMap({ ...updated })
    } catch {}
  }

  const openPushPreview = async () => {
    const toPush = filtered.filter(item => {
      const k = [item['taskNo'] || '', item['cw Date'] || '', item['ma'] || '', item['position'] || ''].join('\x00')
      return selectedKeys.has(k)
    })
    if (!toPush.length) return

    setPreviewLoading(true)
    setPushResult(null)

    const baseItems = toPush.map(item => ({
      taskNumber: item['taskNo'] || '',
      taskName:   [item['position'], item['description'] || item['taskNo']].filter(Boolean).join(' — '),
    }))

    const [{ data: overrides }, familyRegs] = await Promise.all([
      supabase.from('task_cost_overrides').select('*'),
      getFamilyRegs(registration),
    ])
    const ov = overrides || []

    const enriched = await Promise.all(baseItems.map(async row => {
      const match = findOverrideForTask(ov, row.taskNumber, row.taskName)
      if (match) {
        const labor_hours = match.labor_hours ?? null
        return {
          task_number: row.taskNumber,
          task_name:   row.taskName,
          labor_hours,
          labor_cost:  calcLaborCost(labor_hours),
          parts_cost:  match.parts_cost ?? null,
          parts:       match.parts ?? null,
          _note:       'Manually set',
        }
      }

      // Fall back to history medians
      let rows = await searchTaskHistory(row.taskNumber, familyRegs)
      if (!rows.length) rows = await searchTaskHistory(row.taskName, familyRegs)
      if (rows.length) {
        const stats = computeHistoryStats(rows)
        const labor_hours = stats.hours?.median ?? null
        return {
          task_number: row.taskNumber,
          task_name:   row.taskName,
          labor_hours,
          labor_cost:  calcLaborCost(labor_hours),
          parts_cost:  stats.parts?.median ?? null,
          _note:       null,
        }
      }

      return {
        task_number: row.taskNumber,
        task_name:   row.taskName,
        labor_hours: null,
        labor_cost:  null,
        parts_cost:  null,
        _note:       null,
      }
    }))

    setPreviewLoading(false)
    setPushPreviewItems(enriched)
  }

  const formatForWO = () => {
    const toFormat = filtered.filter(item => {
      const k = [item['taskNo'] || '', item['cw Date'] || '', item['ma'] || '', item['position'] || ''].join('\x00')
      return selectedKeys.has(k)
    })
    if (!toFormat.length) return
    const text = toFormat.map(item => {
      const taskName = [item['position'], item['description'] || item['taskNo']].filter(Boolean).join(' — ')
      return `TASK #: ${item['taskNo'] || ''}\n${taskName}`
    }).join('\n\n')
    navigator.clipboard.writeText(text).then(() => {
      setFormatCopied(true)
      setTimeout(() => setFormatCopied(false), 2000)
    })
  }


  const fetchActuals = (reg) => {
    if (!reg || !ACTUALS_URL) return
    setActualsLoading(true)
    fetch(`${ACTUALS_URL}?registration=${encodeURIComponent(reg)}`, { headers: authHeaders })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => {
        const parsed = parseActuals(data)
        setActuals(parsed)
        _cache[reg] = { ..._cache[reg], actuals: parsed }
      })
      .catch(status => {
        // On auth/enrollment errors, use cached actuals rather than clearing them.
        // _cache[reg].actuals may already be populated by fetchDueList's cache fallback.
        if (status === 401 || status === 403 || status === 404) {
          if (_cache[reg]?.actuals?.length) return // already set by due-list cache fallback
          supabase
            .from('traxxall_due_cache')
            .select('actuals')
            .eq('registration', reg)
            .maybeSingle()
            .then(({ data: row }) => {
              if (row?.actuals) setActuals(parseActuals(row.actuals))
            })
            .catch(() => {})
          return
        }
        setActuals([])
      })
      .finally(() => setActualsLoading(false))
  }

  const fetchDueList = (reg) => {
    if (!reg || !DUE_LIST_URL) return
    setLoading(true)
    setError(null)
    setStaleWarning(null)
    // Always request 730 days from the API so all filter options work without re-fetching.
    // The horizon filter below then narrows the display client-side.
    fetch(`${DUE_LIST_URL}?registration=${encodeURIComponent(reg)}&days=730`, { headers: authHeaders })
      .then(async r => {
        if (!r.ok) {
          let detail = ''
          try { const b = await r.json(); detail = b.message || b.error || '' } catch {}
          // On auth/enrollment errors, fall back to the Supabase persistent cache before surfacing an error.
          if (r.status === 401 || r.status === 403 || r.status === 404) {
            return supabase
              .from('traxxall_due_cache')
              .select('items, actuals, fetched_at')
              .eq('registration', reg)
              .maybeSingle()
              .then(({ data: row }) => {
                if (row?.items?.length) {
                  const actuals = parseActuals(row.actuals || [])
                  _cache[reg] = { items: row.items, actuals, lastFetched: new Date(row.fetched_at) }
                  setItems(withComponentPrefixes(row.items))
                  setActuals(actuals)
                  setLastFetched(new Date(row.fetched_at))
                  setFetchedLive(false)
                  const ageHrs = Math.round((Date.now() - new Date(row.fetched_at)) / (1000 * 60 * 60))
                  setStaleWarning(`Live Traxxall sync unavailable — showing data from fleet sync${ageHrs > 0 ? ` (${ageHrs}h ago)` : ''}.`)
                  setLoading(false)
                  return null
                }
                if (r.status === 401) throw new Error(`Aircraft not enrolled in Traxxall.${detail ? ` (${detail})` : ''}`)
                if (r.status === 403) throw new Error(`Aircraft not enrolled in Traxxall fleet.${detail ? ` (${detail})` : ''}`)
                throw new Error(`Aircraft registration not found in Traxxall.${detail ? ` (${detail})` : ''}`)
              })
          }
          throw new Error(`Traxxall API returned ${r.status}.${detail ? ` ${detail}` : ''}`)
        }
        return r.json()
      })
      .then(data => {
        if (data === null) return // cache fallback already handled state
        const list = Array.isArray(data) ? data : []
        const now = new Date()
        setItems(withComponentPrefixes(list))
        setLastFetched(now)
        setFetchedLive(true)
        setLoading(false)
        _cache[reg] = { ..._cache[reg], items: list, lastFetched: now }
      })
      .catch(err => {
        const msg = err.message || ''
        setError(msg.toLowerCase().includes('failed to fetch') || msg.toLowerCase().includes('network')
          ? 'Unable to reach the Traxxall API — CORS restriction. Contact support to set up a Supabase Edge Function.'
          : err.message)
        setLoading(false)
      })
  }

  const fetchEbis = (reg) => {
    if (_ebisAllRows) {
      setDiscrepItems(getDiscrepItemsForReg(_ebisAllRows, reg))
      return
    }
    fetch(`${EBIS_WO_URL}?endpoint=listing`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({ WoStatusID: -1, WoTypeID: 1 }),
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        _ebisAllRows = Array.isArray(data) ? data : []
        setDiscrepItems(getDiscrepItemsForReg(_ebisAllRows, reg))
      })
      .catch(() => {})
  }

  const handleRefresh = (reg) => {
    delete _cache[reg] // bust module cache so fetch is truly fresh, not served from memory
    setFetchedLive(false)
    fetchDueList(reg)
    fetchActuals(reg)
  }

  useEffect(() => {
    // Always reset UI filters when registration changes
    setSearch('')
    setTypeFilter('All')
    setMaFilter('All')
    setError(null)
    setStaleWarning(null)

    if (_cache[registration]) {
      // Restore from module cache — no API call needed
      setItems(withComponentPrefixes(_cache[registration].items))
      setActuals(_cache[registration].actuals || [])
      setLastFetched(_cache[registration].lastFetched)
      setLoading(false)
    } else {
      setItems([])
      setActuals([])
      // Check Supabase persistent cache before hitting live API
      supabase
        .from('traxxall_due_cache')
        .select('items, actuals, fetched_at')
        .eq('registration', registration)
        .maybeSingle()
        .then(({ data: row }) => {
          if (row?.items?.length) {
            const actuals = parseActuals(row.actuals || [])
            _cache[registration] = { items: row.items, actuals, lastFetched: new Date(row.fetched_at) }
            setItems(withComponentPrefixes(row.items))
            setActuals(actuals)
            setLastFetched(new Date(row.fetched_at))
            setLoading(false)
            setActualsLoading(false)
          } else {
            fetchDueList(registration)
            fetchActuals(registration)
          }
        })
        .catch(() => {
          fetchDueList(registration)
          fetchActuals(registration)
        })
    }

    // EBIS planned/unplanned — independent of Traxxall cache
    if (_ebisAllRows) {
      setDiscrepItems(getDiscrepItemsForReg(_ebisAllRows, registration))
    } else {
      setDiscrepItems([])
      fetchEbis(registration)
    }

    // Manual overrides for Review items
    fetchOverrides(registration)
  }, [registration])

  // Fetch WO-specific discrepancy items for all linked work orders.
  // Priority per WO: session cache → Supabase persistent cache → live EBIS fetch.
  // The live fallback handles completed projects excluded from the hourly auto-sync.
  useEffect(() => {
    if (!workOrderNumbers.length || !SUPABASE_URL) { setWoDiscrepItems([]); return }

    const fetchOne = async (wo) => {
      if (_woItemsCache[wo]) return
      const headers = { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` }
      try {
        const r = await fetch(
          `${SUPABASE_URL}/rest/v1/ebis_wo_cache?work_order=eq.${encodeURIComponent(wo)}&select=payload,fetched_at&limit=1`,
          { headers }
        )
        const rows = r.ok ? await r.json() : []
        if (rows.length > 0) {
          _woItemsCache[wo] = buildWoDiscrepItems(rows[0].payload, wo)
          woPayloadStore[wo] = { payload: rows[0].payload, cachedAt: rows[0].fetched_at }
          return
        }
      } catch {}
      // Live fallback
      try {
        const r = await fetch(`${EBIS_WO_URL}?endpoint=detail`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ workOrderNumber: wo }),
        })
        const payload = r.ok ? await r.json() : null
        if (payload) {
          _woItemsCache[wo] = buildWoDiscrepItems(payload, wo)
          woPayloadStore[wo] = { payload, cachedAt: null }
        }
      } catch {}
    }

    Promise.allSettled(workOrderNumbers.map(fetchOne)).then(() => {
      // Preserve all items across WOs without deduplication — DueListItem cross-checks them.
      setWoDiscrepItems(workOrderNumbers.flatMap(wo => _woItemsCache[wo] || []))
    })
  }, [workOrderNumbers.join(',')])

  // WO items take full precedence; listing items fill in only tasks not covered by any WO entry.
  // WO items are NOT deduplicated here — the same task can appear from multiple WOs and
  // DueListItem needs all of them to do cross-WO signoff checking.
  const combinedDiscrepItems = useMemo(() => {
    const woTexts = new Set(woDiscrepItems.map(d => d.text))
    return [...woDiscrepItems, ...discrepItems.filter(d => !woTexts.has(d.text))]
  }, [woDiscrepItems, discrepItems])

  const actualsMap      = useMemo(() => buildActualsMap(actuals), [actuals])
  const taskTypes       = useMemo(() => ['All', ...new Set(items.map(i => i['task Type']).filter(Boolean))], [items])
  const majorAssemblies = useMemo(() => ['All', ...new Set(items.map(i => i['ma']).filter(Boolean))],        [items])

  const filtered = useMemo(() => {
    let list = overviewOnly ? items.filter(isOverviewItem) : items
    // Horizon filter: include OVD/past items + items within the selected window
    if (horizonDays !== Infinity) {
      const cutoff = new Date()
      cutoff.setDate(cutoff.getDate() + horizonDays)
      list = list.filter(i => {
        const d = parseTraxxallDate(i['estimated Due Date'])
        return !d || d <= cutoff
      })
    }
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter(i =>
        (i['description'] || '').toLowerCase().includes(q) ||
        (i['taskNo']      || '').toLowerCase().includes(q) ||
        (i['ata']         || '').toLowerCase().includes(q)
      )
    }
    if (typeFilter !== 'All') list = list.filter(i => i['task Type'] === typeFilter)
    if (maFilter   !== 'All') list = list.filter(i => i['ma']        === maFilter)
    // Primary: urgency rank (overdue > tol-ovd > warning > ok) so in-tolerance items
    // always sort above non-urgent items regardless of their raw due date.
    // Secondary: effective due date (soonest first within each urgency tier).
    return [...list].sort((a, b) => {
      const ra = itemUrgencyRank(a, actualsMap, registration)
      const rb = itemUrgencyRank(b, actualsMap, registration)
      if (ra !== rb) return rb - ra
      const da = sortPriority(a, projections, registration)
      const db = sortPriority(b, projections, registration)
      if (da === Infinity && db === Infinity) return 0
      if (da === Infinity) return 1
      if (db === Infinity) return -1
      return sortDir === 'asc' ? da - db : db - da
    })
  }, [items, horizonDays, search, typeFilter, maFilter, sortDir, actualsMap, projections, overviewOnly])

  // Auto-deselect any selected items that are now known to be in EBIS or complete.
  useEffect(() => {
    if (!combinedDiscrepItems.length) return
    setSelectedKeys(prev => {
      if (!prev.size) return prev
      const next = new Set(prev)
      filtered.forEach(item => {
        const projKey = [item['taskNo'] || '', item['cw Date'] || '', item['ma'] || '', item['position'] || ''].join('\x00')
        if (isMatchedInEbis(item, combinedDiscrepItems)) { next.delete(projKey); return }
        const ov = overridesMap[buildItemKey(registration, item)] ?? null
        if (computeItemPlanStatus(item, combinedDiscrepItems, ov, projectStatus === 'In Work') === 'complete') next.delete(projKey)
      })
      return next.size === prev.size ? prev : next
    })
  }, [combinedDiscrepItems, filtered, overridesMap, projectStatus, registration])

  const overallColor = items.length > 0 ? items[0]['aircraft Color'] : null
  // Use live-actuals-based urgency so badge counts match what is shown on screen.
  const overdueCt = useMemo(
    () => items.filter(i => {
      const u = urgencyFromRows(stripCalendarForAndLogic(buildUomRows(i, actualsMap), i['taskNo'], registration))
      return u === 'overdue' || u === 'deficient'
    }).length,
    [items, actualsMap, registration]
  )
  // "Tol." = items where any non-approx row has tolerance remaining (hard limit hit, tol window open)
  const tolCt = useMemo(
    () => items.filter(i => buildUomRows(i, actualsMap).some(r => !r.isApprox && r.tolRem != null)).length,
    [items, actualsMap]
  )

  useEffect(() => {
    onCountsUpdate?.(overdueCt, tolCt)
  }, [overdueCt, tolCt])

  if (!registration) {
    return <div className="flex items-center justify-center py-24 text-gray-400 text-sm">No aircraft linked to this project.</div>
  }
  if (!SUPABASE_URL) {
    return <div className="flex items-center justify-center py-24 text-sm text-gray-500">Supabase URL not configured</div>
  }

  return (
    <div className="space-y-3">
      {/* Status bar */}
      <div className="flex items-center gap-4 flex-wrap">
        {overallColor && AIRCRAFT_STATUS[overallColor] && (
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${AIRCRAFT_STATUS[overallColor].pill}`}>
            <div className={`w-2 h-2 rounded-full ${AIRCRAFT_STATUS[overallColor].dot}`} />
            <span className={`text-xs font-semibold ${AIRCRAFT_STATUS[overallColor].label}`}>Aircraft Status: {overallColor}</span>
          </div>
        )}
        {!hideCountBadges && (ovdCountProp ?? overdueCt) > 0 && (
          <span className="text-xs font-semibold text-red-600 bg-red-50 px-3 py-1.5 rounded-full">{ovdCountProp ?? overdueCt} OVD</span>
        )}
        {!hideCountBadges && (tolCountProp ?? tolCt) > 0 && (
          <span className="text-xs font-semibold text-orange-700 bg-orange-50 px-3 py-1.5 rounded-full">{tolCountProp ?? tolCt} Tol.</span>
        )}
        {!hideCountBadges && scheduleCount != null && scheduleCount > 0 && (
          <span className="text-xs font-semibold text-amber-700 bg-amber-50 px-3 py-1.5 rounded-full">{scheduleCount} Schedule</span>
        )}
        <div className="flex-1" />
        {lastFetched && !loading && (
          <span className="text-xs text-gray-400 flex items-center gap-1">
            {fetchedLive
              ? <><span className="text-green-600 font-medium">Live</span> · {lastFetched.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</>
              : `As of ${lastFetched.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${lastFetched.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
            }
          </span>
        )}
        <button
          onClick={() => handleRefresh(registration)}
          disabled={loading}
          className="no-print flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors disabled:opacity-40"
        >
          <svg className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {loading ? 'Syncing…' : 'Refresh'}
        </button>
      </div>

      <ActualsStrip actuals={actuals} loading={actualsLoading} />

      {/* Filters */}
      <div className="no-print flex items-center gap-2 flex-wrap">
        <div className="relative">
          <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search description, task #, ATA…"
            className="text-sm border border-gray-200 rounded-lg pl-8 pr-3 py-1.5 focus:outline-none focus:border-brand-red w-56"
          />
        </div>
        <select value={horizonDays} onChange={e => setHorizonDays(Number(e.target.value))}
          className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-brand-red bg-white">
          <option value={30}>30 days</option>
          <option value={90}>90 days</option>
          <option value={180}>180 days</option>
        </select>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-brand-red bg-white">
          {taskTypes.map(t => <option key={t}>{t}</option>)}
        </select>
        <select value={maFilter} onChange={e => setMaFilter(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-brand-red bg-white">
          {majorAssemblies.map(m => <option key={m}>{m}</option>)}
        </select>
        <button
          onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
          className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-500 hover:bg-gray-50 flex items-center gap-1"
        >
          {sortDir === 'asc' ? 'Soonest first' : 'Latest first'}
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={sortDir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'} />
          </svg>
        </button>
        {(search || typeFilter !== 'All' || maFilter !== 'All') && (
          <button onClick={() => { setSearch(''); setTypeFilter('All'); setMaFilter('All') }}
            className="text-xs text-brand-red hover:text-brand-red-dark">Clear filters</button>
        )}
        <span className="text-xs text-gray-400 ml-auto">{filtered.length} item{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Push to EBIS strip */}
      {filtered.length > 0 && (() => {
        const selectableItems = filtered.filter(item => {
          const ov = overridesMap[buildItemKey(registration, item)] ?? null
          return computeItemPlanStatus(item, combinedDiscrepItems, ov, projectStatus === 'In Work') === 'unplanned'
        })
        const selectableKeys  = selectableItems.map(item =>
          [item['taskNo'] || '', item['cw Date'] || '', item['ma'] || '', item['position'] || ''].join('\x00')
        )
        const allSelectableChosen = selectableKeys.length > 0 && selectableKeys.every(k => selectedKeys.has(k))
        return (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-sm">
          <span className="font-medium text-brand-red">Push to EBIS:</span>
          <select
            value={pushMode}
            onChange={e => { setPushMode(e.target.value); setPushResult(null); setSelectedExistingWo(''); setManualWoEntry('') }}
            className="border border-gray-200 rounded-lg px-2 py-1 text-xs bg-white text-gray-700"
          >
            <option value="new-wo">New work order</option>
            <option value="existing">Add to existing</option>
          </select>
          {pushMode === 'existing' && (
            existingWosLoading
              ? <span className="text-xs text-gray-400 italic">Loading…</span>
              : <>
                  {existingWos.length > 0 && (
                    <select
                      value={selectedExistingWo}
                      onChange={e => { setSelectedExistingWo(e.target.value); setManualWoEntry(''); setPushResult(null) }}
                      className="border border-gray-200 rounded-lg px-2 py-1 text-xs bg-white text-gray-700 max-w-[16rem]"
                    >
                      <option value="">Select WO…</option>
                      {existingWos.map(wo => (
                        <option key={wo.WorkOrder} value={wo.WorkOrder}>{wo.label}</option>
                      ))}
                    </select>
                  )}
                  <input
                    type="text"
                    placeholder={existingWos.length > 0 ? 'or enter quote #' : 'WO / quote #'}
                    value={manualWoEntry}
                    onChange={e => { setManualWoEntry(e.target.value); setSelectedExistingWo(''); setPushResult(null) }}
                    className="border border-gray-200 rounded-lg px-2 py-1 text-xs bg-white text-gray-700 w-32"
                  />
                </>
          )}
          <button
            onClick={() => setSelectedKeys(allSelectableChosen ? new Set() : new Set(selectableKeys))}
            className="text-xs text-brand-red hover:text-brand-red-dark underline"
          >
            {allSelectableChosen ? 'Deselect all' : 'Select all'}
          </button>
          <button
            onClick={formatForWO}
            disabled={selectedKeys.size === 0}
            className="rounded-lg border border-brand-red/30 bg-white px-3 py-1 text-xs font-medium text-brand-red hover:bg-red-50 disabled:opacity-40 transition-colors"
          >
            {formatCopied ? '✓ Copied!' : 'Format for WO'}
          </button>
          <button
            onClick={openPushPreview}
            disabled={previewLoading || selectedKeys.size === 0 || (pushMode === 'existing' && !selectedExistingWo && !manualWoEntry)}
            className="ml-auto rounded-lg bg-brand-red px-3 py-1 text-xs font-medium text-white hover:bg-brand-red-dark disabled:opacity-40"
          >
            {previewLoading ? 'Loading…' : `Push ${selectedKeys.size} item${selectedKeys.size !== 1 ? 's' : ''}…`}
          </button>
          {pushResult && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-green-700 font-medium">
                ✓ {pushMode === 'existing' ? 'Added to' : 'Created'} {pushResult.woNumber}
              </span>
              {onAddToSchedule && (
                <button
                  onClick={() => onAddToSchedule({ workOrderNumber: pushResult.woNumber, registration: pushResult.regNum })}
                  className="rounded-md bg-green-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-green-700"
                >
                  Add to schedule
                </button>
              )}
            </div>
          )}
        </div>
        )
      })()}

      {/* Stale-cache warning (live API unavailable but cache loaded) */}
      {staleWarning && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">{staleWarning}</div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-100 rounded-lg animate-pulse" style={{ opacity: 1 - i * 0.12 }} />
          ))}
        </div>
      )}

      {/* Item blocks */}
      {!loading && !error && (
        filtered.length === 0
          ? <div className="py-12 text-center text-gray-400 text-sm">
              {items.length === 0 ? `No due list items found for ${registration}.` : 'No items match the current filters.'}
            </div>
          : <div className="space-y-1.5">
              {filtered.map((item, i) => {
                const itemKey = buildItemKey(registration, item)
                const projKey = [item['taskNo'] || '', item['cw Date'] || '', item['ma'] || '', item['position'] || ''].join('\x00')
                const projDate = projections?.[projKey]?.projDate || null
                const isSelected = selectedKeys.has(projKey)
                const itemOv = overridesMap[itemKey] ?? null
                const itemStatus = computeItemPlanStatus(item, combinedDiscrepItems, itemOv, projectStatus === 'In Work')
                const isBlocked = itemStatus !== 'unplanned'
                return (
                  <div key={i} className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={isBlocked}
                      title={itemStatus === 'complete' ? 'Task already completed — push a new work order only when the next interval comes due' : itemStatus !== 'unplanned' ? 'Already assigned to an EBIS work order' : undefined}
                      onChange={() => {
                        if (isBlocked) return
                        setSelectedKeys(prev => {
                          const next = new Set(prev)
                          isSelected ? next.delete(projKey) : next.add(projKey)
                          return next
                        })
                      }}
                      className={`mt-3 h-4 w-4 flex-shrink-0 ${isBlocked ? 'cursor-not-allowed opacity-30' : 'cursor-pointer'}`}
                    />
                    <div className={`flex-1 rounded-xl transition-colors ${isSelected ? 'ring-1 ring-brand-red/50' : ''}`}>
                      <DueListItem
                        item={item}
                        actualsMap={actualsMap}
                        discrepItems={combinedDiscrepItems}
                        overrideEntry={overridesMap[itemKey] ?? null}
                        onSaveOverride={(status, notes) => saveOverride(itemKey, status, notes)}
                        onClearOverride={() => clearOverride(itemKey)}
                        isInWork={projectStatus === 'In Work'}
                        onTaskClick={onTaskClick}
                        projDate={projDate}
                        reg={registration}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
      )}

      {/* Push confirm modal */}
      {pushPreviewItems && (
        <EbisPushConfirmModal
          items={pushPreviewItems}
          regNum={pushMode !== 'existing' ? registration : undefined}
          workOrder={pushMode === 'existing' ? (selectedExistingWo || manualWoEntry) : undefined}
          type={pushMode === 'new-wo' ? 'work-order' : 'quote'}
          label={
            pushMode === 'new-wo' ? `New Work Order · ${registration}` :
            `Add to ${selectedExistingWo || manualWoEntry}`
          }
          onClose={() => setPushPreviewItems(null)}
          onSuccess={woNumber => {
            setPushResult({ woNumber, regNum: registration })
            setPushPreviewItems(null)
          }}
          successActions={woNumber => onAddToSchedule && (
            <button
              onClick={() => { setPushPreviewItems(null); onAddToSchedule({ workOrderNumber: woNumber, registration }) }}
              className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
            >
              Add to schedule
            </button>
          )}
        />
      )}
    </div>
  )
}
